import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, MapPin, Target, Settings, Calculator } from 'lucide-react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import OpeningHoursSelector from '../../components/ui/OpeningHoursSelector';
import { offerService, CreateOfferData, Offer } from '../../services/offerService';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

// Types for Create Offer
interface CreateOfferFormData {
  offerType: 'restaurant' | 'attraction'; // NEW: First question - offer type
  title: string;
  description: string;
  category: string;
  partnerId: string;
  address: string;
  regularPrice: number;
  discountType: 'percentage' | 'amount' | 'free_goodie' | 'one_plus_one';
  discountPercentage: number;
  discountAmount: number;
  freeGoodieDescription: string;
  redemptionMode: 'qr_upload' | 'barcode_upload' | 'auto_generate' | 'custom_code' | 'url';
  customRedemptionCode: string;
  redemptionUrl: string;
  redemptionType: 'amount' | 'unlimited' | 'user_limit';
  maxRedemptions: number;
  userRedemptionLimit: number;
  userRedemptionResetDays: number;
  operatingHours: string;
  startDate: string;
  endDate: string;
  headerImage: string;
  logoImage: string;
  barcodeFile: File | null;
  qrFile: File | null;
  customTermsConditions: string;
}

interface OfferPreview {
  title: string;
  description: string;
  discountPercentage: number;
  discountAmount: number;
  discountType: string;
  regularPrice: number;
  calculatedSavings: number;
  category: string;
  address: string;
  headerImage: string;
}

interface MapCoordinates {
  lat: number;
  lng: number;
}

const PartnerCreateOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { partner, loading: authLoading } = usePartnerAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapCoordinates, setMapCoordinates] = useState<MapCoordinates | null>(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const getInitialFormData = (): CreateOfferFormData => ({
    offerType: 'attraction', // NEW: Default offer type
    title: '',
    description: '',
    category: '',
    partnerId: partner?.id || '',
    address: '',
    regularPrice: 0,
    discountType: 'percentage',
    discountPercentage: 0,
    discountAmount: 0,
    freeGoodieDescription: '',
    redemptionMode: 'qr_upload',
    customRedemptionCode: '',
    redemptionUrl: '',
    redemptionType: 'unlimited',
    maxRedemptions: 0,
    userRedemptionLimit: 1,
    userRedemptionResetDays: 30,
    operatingHours: '',
    startDate: '',
    endDate: '',
    headerImage: '',
    logoImage: '',
    barcodeFile: null,
    qrFile: null,
    customTermsConditions: 'Offer subject to availability. Terms and conditions apply. Please present this offer at time of purchase. Cannot be combined with other promotions.'
  });

  const [formData, setFormData] = useState<CreateOfferFormData>(getInitialFormData());
  
  const [barcodePreviewUrl, setBarcodePreviewUrl] = useState<string>('');
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string>('');

  const [previewMode, setPreviewMode] = useState('mobile');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBarcode, setUploadingBarcode] = useState(false);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [loadingOffer, setLoadingOffer] = useState(false);

  const categories = [
    'Attractions',
    'Shopping / Retail',
    'Beauty & Wellness',
    'Activities & Experiences',
    'Entertainment & Nightlife',
    'Transport & Mobility',
    'Local Services',
    'Kids & Family'
  ];

  // NEW: Restaurant type options for gastronomy offers
  const restaurantTypes = [
    'Austrian Cuisine',
    'Italian Cuisine',
    'Asian Cuisine',
    'Mediterranean Cuisine',
    'Vegan & Vegetarian',
    'Fast Food & Casual',
    'Fine Dining',
    'Café & Coffee Shop',
    'Bar & Cocktails',
    'Nightclub',
    'Pub & Beer Garden',
    'Wine Bar',
    'Bakery & Pastry'
  ];

  // Check for edit mode and load offer data
  useEffect(() => {
    const editId = searchParams.get('edit');
    const offerFromState = location.state?.offer as Offer;

    if (editId || offerFromState) {
      setIsEditMode(true);
      setEditingOfferId(editId || offerFromState?.id || null);
      
      if (offerFromState) {
        populateFormWithOffer(offerFromState);
      } else if (editId) {
        loadOfferForEdit(editId);
      }
    } else {
      setIsEditMode(false);
      setEditingOfferId(null);
      setFormData(getInitialFormData());
      setBarcodePreviewUrl('');
      setQrPreviewUrl('');
    }
  }, [searchParams, location.state, partner]);

  // Function to populate form with offer data
  const populateFormWithOffer = (offer: Offer) => {
    setFormData({
      offerType: offer.offerType || 'attraction', // FIXED: Set offerType from loaded offer
      title: offer.title || '',
      description: offer.description || '',
      category: offer.category || '',
      partnerId: offer.partnerId || partner?.id || '',
      address: offer.address || '',
      regularPrice: offer.regularPrice || 0,
      discountType: (offer.discountType as 'percentage' | 'amount' | 'free_goodie' | 'one_plus_one') || 'percentage',
      discountPercentage: offer.discountPercentage || 0,
      discountAmount: offer.discountAmount || 0,
      freeGoodieDescription: offer.freeGoodieDescription || '',
      redemptionMode: (offer.redemptionMode as 'qr_upload' | 'barcode_upload' | 'auto_generate' | 'custom_code' | 'url') || 'auto_generate',
      customRedemptionCode: offer.customRedemptionCode || '',
      redemptionUrl: offer.customRedemptionCode && offer.redemptionMode === 'url' ? offer.customRedemptionCode : '',
      redemptionType: (offer.redemptionType as 'amount' | 'unlimited' | 'user_limit') || 'unlimited',
      maxRedemptions: offer.maxRedemptions || 0,
      userRedemptionLimit: 1,
      userRedemptionResetDays: 30,
      operatingHours: offer.operatingHours || '',
      startDate: '',
      endDate: offer.expiresAt ? new Date(offer.expiresAt).toISOString().split('T')[0] : '',
      headerImage: offer.imageUrl || '',
      logoImage: offer.businessLogoUrl || '',
      barcodeFile: null,
      qrFile: null,
      customTermsConditions: 'Offer subject to availability. Terms and conditions apply. Please present this offer at time of purchase. Cannot be combined with other promotions.'
    });

    if (offer.barcodeUrl) {
      if (offer.redemptionMode === 'barcode_upload') {
        setBarcodePreviewUrl(offer.barcodeUrl);
      } else if (offer.redemptionMode === 'qr_upload') {
        setQrPreviewUrl(offer.barcodeUrl);
      }
    }

    if (offer.latitude && offer.longitude) {
      setMapCoordinates({
        lat: offer.latitude,
        lng: offer.longitude
      });
    }
  };

  // Function to load offer by ID for editing
  const loadOfferForEdit = async (offerId: string) => {
    setLoadingOffer(true);
    setError('');
    
    try {
      const offer = await offerService.getOfferById(offerId);
      
      if (offer) {
        // Verify that this offer belongs to the current partner
        if (offer.partnerId !== partner?.id) {
          setError('You do not have permission to edit this offer.');
          return;
        }
        populateFormWithOffer(offer);
      } else {
        setError('Offer not found.');
      }
    } catch (err: any) {
      setError(`Failed to load offer: ${err.message}`);
    } finally {
      setLoadingOffer(false);
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !partner) {
      navigate('/partner-login');
    }
  }, [partner, authLoading, navigate]);

  const handleInputChange = (field: keyof CreateOfferFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'header' | 'logo' | 'barcode' | 'qr') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // CRITICAL FIX: Add detailed logging for image upload process
    console.log('=== FILE UPLOAD STARTED ===');
    console.log('Upload type:', type);
    console.log('File name:', file.name);
    console.log('File size:', file.size, 'bytes');
    console.log('File type:', file.type);

    try {
      if (type === 'header' || type === 'logo') {
        setUploadingImage(true);
        const folder = type === 'header' ? 'headers' : 'logos';
        
        console.log('Uploading to folder:', folder);
        const imageUrl = await offerService.uploadOfferImage(file, folder);
        
        console.log('✅ Upload successful! Image URL:', imageUrl);
        
        // CRITICAL FIX: Verify URL is not empty before setting state
        if (!imageUrl || imageUrl.trim() === '') {
          throw new Error('Upload returned empty URL');
        }
        
        if (type === 'header') {
          setFormData(prev => ({ ...prev, headerImage: imageUrl }));
          console.log('Header image URL saved to form state');
        } else {
          setFormData(prev => ({ ...prev, logoImage: imageUrl }));
          console.log('Logo image URL saved to form state');
        }

        // CRITICAL FIX: Show success message to user
        setSuccess(`${type === 'header' ? 'Header' : 'Logo'} image uploaded successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      } else if (type === 'barcode' || type === 'qr') {
        setUploadingBarcode(true);
        
        const previewUrl = URL.createObjectURL(file);
        
        if (type === 'barcode') {
          setBarcodePreviewUrl(previewUrl);
          setFormData(prev => ({ ...prev, barcodeFile: file }));
        } else {
          setQrPreviewUrl(previewUrl);
          setFormData(prev => ({ ...prev, qrFile: file }));
        }
        
        setUploadingBarcode(false);
      }
      
      console.log('=== FILE UPLOAD COMPLETE ===');
    } catch (err: any) {
      console.error('❌ FILE UPLOAD FAILED:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploadingImage(false);
      setUploadingBarcode(false);
    }
  };

  const handleAddressChange = async (address: string) => {
    setFormData(prev => ({ ...prev, address }));
    
    if (address.length > 10) {
      setGeocodingLoading(true);
      try {
        const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        
        if (googleMapsApiKey && googleMapsApiKey !== 'your-google-maps-api-key-here') {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.results && data.results.length > 0) {
              const location = data.results[0].geometry.location;
              setMapCoordinates({ lat: location.lat, lng: location.lng });
            } else {
              setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
            }
          } else {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          }
        } else {
          if (address.toLowerCase().includes('vienna') || address.toLowerCase().includes('wien')) {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          } else {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          }
        }
      } catch (err) {
        console.error('Geocoding failed:', err);
        setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
      } finally {
        setGeocodingLoading(false);
      }
    } else {
      setMapCoordinates(null);
    }
  };

  const calculateSavings = (): number => {
    const { regularPrice, discountType, discountPercentage, discountAmount } = formData;
    
    if (!regularPrice || regularPrice <= 0) return 0;
    
    if (discountType === 'percentage' && discountPercentage > 0) {
      return (regularPrice * discountPercentage) / 100;
    } else if (discountType === 'amount' && discountAmount > 0) {
      return Math.min(discountAmount, regularPrice);
    } else if (discountType === 'one_plus_one') {
      return regularPrice;
    }
    
    return 0;
  };

  const calculateFinalPrice = (): number => {
    const { regularPrice, discountType } = formData;
    
    if (discountType === 'one_plus_one') {
      return regularPrice;
    }
    
    const savings = calculateSavings();
    return Math.max(0, regularPrice - savings);
  };

  const handlePublish = async () => {
    if (!partner) {
      setError('Please log in to create offers');
      navigate('/partner-login');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Validate required fields
      if (!formData.title || !formData.description || !formData.category) {
        throw new Error('Please fill in all required fields');
      }

      if (!formData.regularPrice || formData.regularPrice <= 0) {
        throw new Error('Please enter a valid regular price');
      }

      // Additional validation for custom redemption codes
      if (formData.redemptionMode === 'custom_code') {
        if (!formData.customRedemptionCode || formData.customRedemptionCode.trim().length < 3) {
          throw new Error('Custom redemption code must be at least 3 characters long');
        }
        if (!/^[A-Z0-9_-]+$/i.test(formData.customRedemptionCode)) {
          throw new Error('Custom redemption code can only contain letters, numbers, hyphens, and underscores');
        }
      }

      // NEW: Validation for URL redemption mode
      if (formData.redemptionMode === 'url') {
        if (!formData.redemptionUrl || formData.redemptionUrl.trim().length < 5) {
          throw new Error('Please enter a valid URL for redemption');
        }
        // Basic URL validation
        try {
          new URL(formData.redemptionUrl);
        } catch {
          throw new Error('Please enter a valid URL (e.g., https://example.com/offer)');
        }
      }

      // CRITICAL FIX: Log image URLs before publishing
      console.log('=== OFFER PUBLISH DEBUG ===');
      console.log('Form Data Image URLs:', {
        headerImage: formData.headerImage,
        logoImage: formData.logoImage
      });

      // CRITICAL FIX: Verify at least one required field has data
      if (!formData.headerImage && !formData.logoImage) {
        console.warn('⚠️ WARNING: Publishing offer without any images! This is unusual.');
      }

      let barcodeUrl = '';
      if (formData.redemptionMode === 'barcode_upload' && formData.barcodeFile) {
        barcodeUrl = await offerService.uploadBarcode(formData.barcodeFile);
      } else if (formData.redemptionMode === 'qr_upload' && formData.qrFile) {
        barcodeUrl = await offerService.uploadBarcode(formData.qrFile);
      }

      const offerData: CreateOfferData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        offerType: formData.offerType, // FIXED: Pass offerType to service
        partnerId: partner.id, // Always use partner's ID from context
        address: formData.address || null,
        regularPrice: formData.regularPrice,
        discountType: formData.discountType,
        discountPercentage: formData.discountPercentage || 0,
        discountAmount: formData.discountAmount || 0,
        freeGoodieDescription: formData.freeGoodieDescription || null,
        redemptionMode: formData.redemptionMode,
        customRedemptionCode: formData.redemptionMode === 'custom_code' ? formData.customRedemptionCode : (formData.redemptionMode === 'url' ? formData.redemptionUrl : undefined),
        redemptionType: formData.redemptionType,
        maxRedemptions: formData.redemptionType === 'amount' ? formData.maxRedemptions : 0,
        userRedemptionLimit: formData.redemptionType === 'user_limit' ? formData.userRedemptionLimit : null,
        userRedemptionResetDays: formData.redemptionType === 'user_limit' ? formData.userRedemptionResetDays : null,
        operatingHours: formData.operatingHours || null,
        imageUrl: formData.headerImage || null, // CRITICAL FIX: Ensure this is passed correctly
        businessLogoUrl: formData.logoImage || null, // CRITICAL FIX: Ensure this is passed correctly
        barcodeUrl: (formData.redemptionMode === 'barcode_upload' || formData.redemptionMode === 'qr_upload') && barcodeUrl ? barcodeUrl : undefined,
        expiresAt: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        status: 'active'
      };

      // CRITICAL FIX: Log the complete offerData being sent to service
      console.log('Offer Data being sent to service:', JSON.stringify({
        ...offerData,
        imageUrl: offerData.imageUrl,
        businessLogoUrl: offerData.businessLogoUrl
      }, null, 2));

      if (isEditMode && editingOfferId) {
        await offerService.updateOffer(editingOfferId, offerData);
        setSuccess('Offer updated successfully!');
      } else {
        const createdOffer = await offerService.createOffer(offerData);
        console.log('✅ Offer created. Returned data:', createdOffer);
        setSuccess('Offer created successfully!');
      }

      setTimeout(() => navigate('/partner-dashboard'), 1500);
    } catch (error: any) {
      console.error('❌ Error publishing offer:', error);
      if (error.message === 'Not authenticated') {
        setError('Authentication expired. Please log in again.');
        setTimeout(() => navigate('/partner-login'), 2000);
      } else if (error.message.includes('QR code conflict') || error.message.includes('already in use')) {
        setError(`${error.message} Please try again or use a different custom code.`);
      } else if (error.message.includes('Custom redemption code') || error.message.includes('regular price')) {
        setError(error.message);
      } else {
        setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} offer`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOfferPreview = (): OfferPreview => {
    return {
      title: formData.title || 'Offer Title Preview',
      description: formData.description || 'Offer description will appear here...',
      discountPercentage: formData.discountPercentage || 0,
      discountAmount: formData.discountAmount || 0,
      discountType: formData.discountType,
      regularPrice: formData.regularPrice || 0,
      calculatedSavings: calculateSavings(),
      category: formData.category || 'Category',
      address: formData.address || 'Location',
      headerImage: formData.headerImage || 'https://images.unsplash.com/photo-1708782340354-f77e721d1ef8'
    };
  };

  const renderMapPreview = () => {
    if (!formData.address) {
      return (
        <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Enter address to see map preview
            </p>
          </div>
        </div>
      );
    }

    if (geocodingLoading) {
      return (
        <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">
              Loading map preview...
            </p>
          </div>
        </div>
      );
    }

    if (mapCoordinates) {
      const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (!googleMapsApiKey || googleMapsApiKey === 'your-google-maps-api-key-here') {
        return (
          <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 font-medium">
                Google Maps API Key Required
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Add VITE_GOOGLE_MAPS_API_KEY to your .env file
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="bg-muted rounded-lg h-48 overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(formData.address)}&zoom=15&maptype=roadmap`}
            title="Location Preview"
          />
        </div>
      );
    }

    return (
      <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Address: {formData.address}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Map preview will appear here
          </p>
        </div>
      </div>
    );
  };

  const preview = renderOfferPreview();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{isEditMode ? 'Edit Offer' : 'Create Offer'} - Partner Portal</title>
        <meta name="description" content="Create and manage your offers as a partner" />
      </Helmet>

      <div className="flex h-screen bg-background">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          userRole="partner"
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header 
            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
            showMobileMenu={sidebarOpen}
            isSidebarCollapsed={sidebarCollapsed}
          />
          <main className="flex-1 overflow-y-auto pt-16 p-6">
            {/* Breadcrumb Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/partner-dashboard')}
                    className="flex items-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Partner Dashboard</span>
                  </Button>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">
                      {isEditMode ? 'Edit Your Offer' : 'Create New Offer'}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      {isEditMode 
                        ? 'Update your existing offer details' : 'Create a promotional offer for your business'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{success}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-8 gap-8">
              {/* Main Form Area - 5 columns */}
              <div className="lg:col-span-5">
                {/* Tab Navigation */}
                <div className="bg-card border border-border rounded-lg mb-6">
                  <div className="flex border-b border-border">
                    {[
                      { id: 'basic', label: 'Basic Details', icon: Target },
                      { id: 'location', label: 'Location & Hours', icon: MapPin },
                      { id: 'pricing', label: 'Pricing & Validity', icon: Calculator },
                      { id: 'redemption', label: 'Redemption Mode', icon: Settings }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                          activeTab === tab.id
                            ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="p-6">
                    {/* Basic Details Tab */}
                    {activeTab === 'basic' && (
                      <div className="space-y-6">
                        {/* NEW: Offer Type Selection - FIRST QUESTION */}
                        <div>
                          <label className="block text-sm font-medium mb-3">
                            Offer Type * <span className="text-xs text-muted-foreground">(This determines how your offer appears in the mobile app)</span>
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                              type="button"
                              onClick={() => handleInputChange('offerType', 'restaurant')}
                              className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all ${
                                formData.offerType === 'restaurant' ? 'border-primary bg-primary/10 text-primary font-medium shadow-md' : 'border-border hover:border-primary/50 hover:bg-muted/50'
                              }`}
                            >
                              <span className="text-4xl mb-3">🍽️</span>
                              <span className="text-lg font-semibold mb-1">Restaurant / Gastronomy</span>
                              <span className="text-xs text-muted-foreground text-center">
                                Restaurants, cafés, bars, and food establishments
                              </span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleInputChange('offerType', 'attraction')}
                              className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all ${
                                formData.offerType === 'attraction' ? 'border-primary bg-primary/10 text-primary font-medium shadow-md' : 'border-border hover:border-primary/50 hover:bg-muted/50'
                              }`}
                            >
                              <span className="text-4xl mb-3">🎭</span>
                              <span className="text-lg font-semibold mb-1">Attraction / Other</span>
                              <span className="text-xs text-muted-foreground text-center">
                                Entertainment, shopping, activities, and all other offers
                              </span>
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            ℹ️ In the mobile app, restaurants and attractions are displayed in separate sections for better user experience
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Offer Title *</label>
                          <Input
                            placeholder="e.g., 20% Off All Menu Items"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Description *</label>
                          <textarea
                            placeholder="Detailed description of the offer..."
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>

                        {/* CONDITIONAL: Show category picker ONLY for attractions */}
                        {formData.offerType === 'attraction' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Category *</label>
                            <Select
                              value={formData.category}
                              onChange={(value) => handleInputChange('category', value)}
                              placeholder="Select category"
                              options={categories.map(category => ({
                                value: category,
                                label: category
                              }))}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Choose the category that best describes your offer
                            </p>
                          </div>
                        )}

                        {/* NEW: Show restaurant type picker ONLY for restaurants */}
                        {formData.offerType === 'restaurant' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              Restaurant Type * <span className="text-xs text-muted-foreground">(Cuisine or establishment type)</span>
                            </label>
                            <Select
                              value={formData.category}
                              onChange={(value) => handleInputChange('category', value)}
                              placeholder="Select restaurant type"
                              options={restaurantTypes.map(type => ({
                                value: type,
                                label: type
                              }))}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Select the type of cuisine or dining experience you offer
                            </p>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium mb-2">Header Image</label>
                          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground mb-2">
                              {uploadingImage ? 'Uploading...' : 'Drag and drop an image, or click to browse'}
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'header')}
                              className="hidden"
                              id="header-image-upload"
                              disabled={uploadingImage}
                            />
                            <Button variant="outline" onClick={() => document.getElementById('header-image-upload')?.click()} disabled={uploadingImage}>
                              Choose File
                            </Button>
                          </div>
                          {formData.headerImage && (
                            <div className="mt-4">
                              <img src={formData.headerImage} alt="Header preview" className="w-32 h-32 object-cover rounded-lg" />
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Logo Image</label>
                          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground mb-2">
                              {uploadingImage ? 'Uploading...' : 'Upload business logo'}
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'logo')}
                              className="hidden"
                              id="logo-image-upload"
                              disabled={uploadingImage}
                            />
                            <Button variant="outline" onClick={() => document.getElementById('logo-image-upload')?.click()} disabled={uploadingImage}>
                              Choose Logo
                            </Button>
                          </div>
                          {formData.logoImage && (
                            <div className="mt-4">
                              <img src={formData.logoImage} alt="Logo preview" className="w-16 h-16 object-cover rounded-lg" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Location & Hours Tab */}
                    {activeTab === 'location' && (
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium mb-2">Address *</label>
                          <Input
                            placeholder="e.g., 123 Main Street, Vienna, Austria"
                            value={formData.address}
                            onChange={(e) => handleAddressChange(e.target.value)}
                            className="w-full"
                          />
                        </div>

                        <OpeningHoursSelector
                          value={formData.operatingHours}
                          onChange={(value) => handleInputChange('operatingHours', value)}
                        />

                        <div className="bg-muted/50 rounded-lg p-4">
                          <h3 className="font-medium mb-2">Map Preview</h3>
                          {renderMapPreview()}
                        </div>
                      </div>
                    )}

                    {/* Pricing & Validity Tab */}
                    {activeTab === 'pricing' && (
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium mb-2">Regular Price (€) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="29.90"
                            value={formData.regularPrice || ''}
                            onChange={(e) => handleInputChange('regularPrice', parseFloat(e.target.value) || 0)}
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-3">Discount Type *</label>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { value: 'percentage', label: 'Percentage Off', icon: '%' },
                              { value: 'amount', label: 'Fixed Amount', icon: '€' },
                              { value: 'free_goodie', label: 'Free Goodie', icon: '🎁' },
                              { value: 'one_plus_one', label: '1 + 1 Offer', icon: '2x' }
                            ].map(type => (
                              <button
                                key={type.value}
                                type="button"
                                onClick={() => handleInputChange('discountType', type.value)}
                                className={`flex items-center justify-center space-x-2 p-3 rounded-lg border-2 transition-all ${
                                  formData.discountType === type.value
                                    ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <span className="text-lg">{type.icon}</span>
                                <span className="text-sm">{type.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {formData.discountType === 'percentage' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Discount Percentage *</label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              placeholder="20"
                              value={formData.discountPercentage || ''}
                              onChange={(e) => handleInputChange('discountPercentage', parseInt(e.target.value) || 0)}
                            />
                          </div>
                        )}

                        {formData.discountType === 'amount' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Discount Amount (€) *</label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="5.00"
                              value={formData.discountAmount || ''}
                              onChange={(e) => handleInputChange('discountAmount', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                        )}

                        {formData.discountType === 'free_goodie' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Free Goodie Description *</label>
                            <Input
                              placeholder="e.g., Free dessert with main course"
                              value={formData.freeGoodieDescription}
                              onChange={(e) => handleInputChange('freeGoodieDescription', e.target.value)}
                            />
                          </div>
                        )}

                        {/* Price Calculation Display */}
                        <div className="bg-muted/50 rounded-lg p-4">
                          <h3 className="font-medium mb-3">Price Calculation</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Regular Price:</span>
                              <span>€{formData.regularPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-green-600">
                              <span>You Save:</span>
                              <span>€{calculateSavings().toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t pt-2">
                              <span>Customer Pays:</span>
                              <span>€{calculateFinalPrice().toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-3">Redemption Limit *</label>
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                            {[
                              { value: 'unlimited', label: 'Unlimited', icon: '∞' },
                              { value: 'amount', label: 'Limited', icon: '#' },
                              { value: 'user_limit', label: 'Per User', icon: '👤' }
                            ].map(type => (
                              <button
                                key={type.value}
                                type="button"
                                onClick={() => handleInputChange('redemptionType', type.value)}
                                className={`flex flex-col items-center space-y-2 p-4 rounded-lg border-2 transition-all ${
                                  formData.redemptionType === type.value
                                    ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <span className="text-2xl">{type.icon}</span>
                                <span className="text-sm font-medium">{type.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {formData.redemptionType === 'amount' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Maximum Redemptions</label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="100"
                              value={formData.maxRedemptions || ''}
                              onChange={(e) => handleInputChange('maxRedemptions', parseInt(e.target.value) || 0)}
                            />
                          </div>
                        )}

                        {formData.redemptionType === 'user_limit' && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Per User Limit</label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="1"
                                value={formData.userRedemptionLimit || ''}
                                onChange={(e) => handleInputChange('userRedemptionLimit', parseInt(e.target.value) || 1)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Reset Days</label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="30"
                                value={formData.userRedemptionResetDays || ''}
                                onChange={(e) => handleInputChange('userRedemptionResetDays', parseInt(e.target.value) || 30)}
                              />
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Start Date</label>
                            <Input
                              type="date"
                              value={formData.startDate}
                              onChange={(e) => handleInputChange('startDate', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">End Date</label>
                            <Input
                              type="date"
                              value={formData.endDate}
                              onChange={(e) => handleInputChange('endDate', e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Custom Terms & Conditions</label>
                          <textarea
                            placeholder="Enter custom terms and conditions for this offer..."
                            value={formData.customTermsConditions}
                            onChange={(e) => handleInputChange('customTermsConditions', e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Specify any special terms, conditions, or restrictions for this offer
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Redemption Mode Tab */}
                    {activeTab === 'redemption' && (
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium mb-3">Redemption Mode *</label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                              { value: 'qr_upload', label: 'QR Code', icon: '📱' },
                              { value: 'barcode_upload', label: 'Barcode', icon: '📊' },
                              { value: 'custom_code', label: 'Custom Code', icon: '✏️' },
                              { value: 'url', label: 'URL', icon: '🔗' }
                            ].map(mode => (
                              <button
                                key={mode.value}
                                type="button"
                                onClick={() => handleInputChange('redemptionMode', mode.value)}
                                className={`flex flex-col items-center space-y-2 p-4 rounded-lg border-2 transition-all ${
                                  formData.redemptionMode === mode.value
                                    ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <span className="text-2xl">{mode.icon}</span>
                                <span className="text-sm">{mode.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {formData.redemptionMode === 'qr_upload' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Upload QR Code</label>
                            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e, 'qr')}
                                className="hidden"
                                id="qr-upload"
                              />
                              <Button variant="outline" onClick={() => document.getElementById('qr-upload')?.click()}>
                                Choose QR Code
                              </Button>
                            </div>
                            {qrPreviewUrl && (
                              <div className="mt-4">
                                <img src={qrPreviewUrl} alt="QR preview" className="w-32 h-32 mx-auto" />
                              </div>
                            )}
                          </div>
                        )}

                        {formData.redemptionMode === 'barcode_upload' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Upload Barcode</label>
                            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e, 'barcode')}
                                className="hidden"
                                id="barcode-upload"
                              />
                              <Button variant="outline" onClick={() => document.getElementById('barcode-upload')?.click()}>
                                Choose Barcode
                              </Button>
                            </div>
                            {barcodePreviewUrl && (
                              <div className="mt-4">
                                <img src={barcodePreviewUrl} alt="Barcode preview" className="w-32 h-32 mx-auto" />
                              </div>
                            )}
                          </div>
                        )}

                        {formData.redemptionMode === 'custom_code' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Custom Code</label>
                            <Input
                              placeholder="e.g., SUMMER2024"
                              value={formData.customRedemptionCode}
                              onChange={(e) => handleInputChange('customRedemptionCode', e.target.value.toUpperCase())}
                            />
                          </div>
                        )}

                        {formData.redemptionMode === 'url' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Redemption URL *</label>
                            <Input
                              placeholder="e.g., https://yourwebsite.com/special-offer"
                              value={formData.redemptionUrl}
                              onChange={(e) => handleInputChange('redemptionUrl', e.target.value)}
                              className="w-full"
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Enter the clickable URL where users will be directed to redeem this offer. This could be a link to your website, booking page, or partner platform.
                            </p>
                            {formData.redemptionUrl && (
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-700">
                                  <strong>Preview:</strong> Users will be redirected to: {formData.redemptionUrl}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/partner-dashboard')}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handlePublish} disabled={loading}>
                      {loading 
                        ? (isEditMode ? 'Updating...' : 'Publishing...') 
                        : (isEditMode ? 'Update Offer' : 'Publish Offer')
                      }
                    </Button>
                  </div>
                </div>
              </div>

              {/* Live Preview Sidebar */}
              <div className="lg:col-span-3">
                <div className="sticky top-24">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Live Preview</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setPreviewMode('mobile')}
                          className={`px-3 py-1 rounded text-xs ${
                            previewMode === 'mobile' ? 'bg-primary text-white' : 'bg-muted'
                          }`}
                        >
                          Mobile
                        </button>
                        <button
                          onClick={() => setPreviewMode('desktop')}
                          className={`px-3 py-1 rounded text-xs ${
                            previewMode === 'desktop' ? 'bg-primary text-white' : 'bg-muted'
                          }`}
                        >
                          Desktop
                        </button>
                      </div>
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="h-32 bg-gray-200 flex items-center justify-center">
                          {preview.headerImage ? (
                            <img src={preview.headerImage} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Header Image</span>
                          )}
                        </div>
                        <div className="p-3">
                          <h4 className="font-semibold text-sm mb-1">{preview.title}</h4>
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">{preview.description}</p>
                          <div className="text-xs">
                            <div className="flex justify-between mb-1">
                              <span>Regular:</span>
                              <span className="line-through">€{preview.regularPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold">
                              <span>Final:</span>
                              <span className="text-green-600">€{calculateFinalPrice().toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {loadingOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading offer...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCreateOffer;